import React, { useCallback, useEffect, useRef, useState } from 'react';

import { warning } from '@/assets/icons';
import { classNames } from '@/helpers/classNames';
import { useMediaDomainUrl } from '@/hooks/useMediaDomainUrl';
import { CircularProgress, SvgIcon } from '@material-ui/core';
import Hls from 'hls.js';

import styles from './Player.module.scss';

type InitialVideoPlayObject = {
	videoElement: React.RefObject<HTMLVideoElement>;
	hls: Hls;
	setVideoReady: React.Dispatch<React.SetStateAction<boolean>>;
	play: () => void;
};

const initialVideoPlay = ({
	videoElement,
	hls,
	setVideoReady,
	play,
}: InitialVideoPlayObject) => {
	if (videoElement.current) {
		hls.startLoad(0);
		videoElement.current.currentTime = 0;
		// hls.currentLevel = Math.floor((hls.levels.length - 1) / 2);
		// hls.currentLevel = -1;
		setVideoReady(true);
		play();
	}
};

interface IProps {
	url: string | null;
	/** Start the video at x seconds */
	posterUrl: string;
	className?: string;
	dimensionsClassName?: string;
	posterClassName?: string;
	description?: string | null;
	contributor?: {
		name?: string | null;
		imageUrl?: string | null;
	};
	noOverlayItems?: boolean;
	onClick?: () => void;
}

export const Player: React.FC<IProps> = ({
	url,
	posterUrl,
	className,
	dimensionsClassName,
	posterClassName,
	description,
	contributor,
	noOverlayItems,
	onClick = () => {},
}) => {
	const [hls] = useState<Hls>(
		new Hls({
			xhrSetup: (xhr) => {
				xhr.withCredentials = false; // do send cookies
				xhr.setRequestHeader(
					'Access-Control-Allow-Headers',
					'X-Requested-With, Content-Type, Accept',
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Origin',
					window.location.origin,
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Credentials',
					'true',
				);
			},
			startLevel: -1,
		}),
	);
	const videoUrl = useMediaDomainUrl(url || '') || null;
	const videoElement = useRef<HTMLVideoElement>(null);
	const [liked, setLiked] = useState(false);
	const [videoReady, setVideoReady] = useState<boolean>(false);
	const [playerSupported, setPlayerSupported] = useState<boolean>(true);
	const [playError, setPlayError] = useState<boolean>(false);
	// remains for a possible later use
	const [playPercentage, setPlayPercentage] = useState<number>(0);

	const applyVideoAction = useCallback(
		(action: (video: HTMLVideoElement) => void): boolean => {
			if (videoElement.current) {
				action(videoElement.current);
				return true;
			}
			return false;
		},
		[],
	);

	const play = useCallback(() => {
		applyVideoAction((video) => {
			setPlayError(false);
			const playPromise = video.play();
			// In browsers that don’t yet support this functionality,
			// playPromise won’t be defined.
			if (playPromise !== undefined) {
				playPromise.catch(function (error) {
					// Automatic playback failed.
					// Show a UI element to let the user manually start playback.
					setPlayError(true);
				});
			}
		});
	}, [applyVideoAction]);

	useEffect(() => {
		return () => {
			hls.destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			videoElement.current &&
			!videoElement.current.canPlayType(
				'application/vnd.apple.mpegurl',
			) &&
			!Hls.isSupported()
		) {
			setPlayerSupported(false);
		}
	}, [videoElement]);

	useEffect(() => {
		if (videoElement.current) {
			if (videoUrl && Hls.isSupported()) {
				hls.loadSource(videoUrl);
				hls.attachMedia(videoElement.current);
				hls.on(Hls.Events.MANIFEST_PARSED, () =>
					initialVideoPlay({
						hls,
						play,
						setVideoReady,
						videoElement,
					}),
				);
			} else if (
				videoElement.current.canPlayType(
					'application/vnd.apple.mpegurl',
				) &&
				videoUrl
			) {
				// Case for Safari
				videoElement.current.src = videoUrl;
				videoElement.current.addEventListener('loadedmetadata', () =>
					initialVideoPlay({
						hls,
						play,
						setVideoReady,
						videoElement,
					}),
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoUrl]);

	useEffect(() => {
		if (videoElement.current) {
			const video = videoElement.current;
			// Attach the events listener to the video element
			video.addEventListener('durationchange', () => {
				setPlayPercentage(video.currentTime / video.duration);
			});
			video.addEventListener('timeupdate', () => {
				setPlayPercentage(video.currentTime / video.duration);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoElement]);

	const isVideoLoading = playPercentage === 0 || isNaN(playPercentage);
	const avatar = contributor?.imageUrl || '/assets/icon/asaph-logo-icon.png';
	const name = contributor?.name || '';

	return (
		<div className={classNames([styles['player'], className])}>
			{!playerSupported ? (
				<div className="player-error">
					<SvgIcon
						component={warning}
						className="player-error__icon"
					/>
					<div className="player-error__text">
						Unfortunately your browser doesn't support HLS files.
					</div>
				</div>
			) : (
				<div
					className={classNames([
						styles['video-dimensions'],
						dimensionsClassName,
					])}
					onClick={() => onClick()}
				>
					<video
						ref={videoElement}
						autoPlay={true}
						playsInline={true}
						muted={true}
						loop
					/>

					{isVideoLoading && (
						<div
							style={{ backgroundImage: `url(${posterUrl})` }}
							className={classNames([
								styles['poster'],
								posterClassName,
							])}
						>
							<CircularProgress />
						</div>
					)}
					{!noOverlayItems && (
						<div className={styles['overlay']}>
							<div className={styles['gradient']} />

							<div className={styles['bottomRow']}>
								<div className={styles['contributor']}>
									<div className={styles['left']}>
										<img
											src={avatar}
											className={styles['avatar']}
										/>
										<div className={styles['details']}>
											<div className={styles['name']}>
												{name}
											</div>
											<div
												className={
													styles['description']
												}
											>
												{description}
											</div>
										</div>
									</div>
									<div className={styles['right']}>
										<div className={styles['play']}>
											<img src="/assets/play.svg" />
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Player;
